import { type TColumnDef, type TFilterDef, type TFilterValue } from "./types";

/** Simple leading-edge-free debounce. */
export function debounce<TArgs extends unknown[]>(
  fn: (...args: TArgs) => void,
  delayMs: number
): ((...args: TArgs) => void) & { cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const debounced = (...args: TArgs) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delayMs);
  };
  debounced.cancel = () => {
    if (timer) clearTimeout(timer);
    timer = null;
  };
  return debounced;
}

/** Whether a filter value counts as "set" (non-empty). */
export function isFilterActive(value: TFilterValue): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "boolean") return true;
  // dateRange
  return Boolean(value.from) || Boolean(value.to);
}

/**
 * Maps filter values → flat query params keyed by each filter's `param`.
 * dateRange expands to `<param>_from` / `<param>_to`.
 */
export function filtersToParams(
  filters: TFilterDef[],
  values: Record<string, TFilterValue>
): Record<string, TFilterValue> {
  const params: Record<string, TFilterValue> = {};
  for (const filter of filters) {
    const value = values[filter.id];
    if (!isFilterActive(value)) continue;
    const param = filter.param ?? filter.id;
    if (filter.type === "dateRange" && typeof value === "object" && !Array.isArray(value)) {
      if (value.from) params[`${param}_from`] = value.from;
      if (value.to) params[`${param}_to`] = value.to;
    } else {
      params[param] = value;
    }
  }
  return params;
}

/** Resolve a column's raw value for a row (accessor or `row[id]`). */
export function getColumnValue<TRow>(column: TColumnDef<TRow>, row: TRow): unknown {
  if (column.accessor) return column.accessor(row);
  return (row as Record<string, unknown>)[column.id];
}

/** Stringify a value for CSV/search purposes. */
function stringifyValue(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean" || typeof value === "bigint") {
    return String(value);
  }
  if (typeof value === "symbol") return value.toString();
  // Objects, arrays, functions.
  try {
    return JSON.stringify(value) ?? "";
  } catch {
    return "";
  }
}

/** Escape a single CSV field per RFC 4180. */
function csvEscape(value: string): string {
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

/** Serialize rows to a CSV string using the visible columns as headers. */
export function rowsToCsv<TRow>(columns: TColumnDef<TRow>[], rows: TRow[]): string {
  const header = columns.map((c) => csvEscape(typeof c.header === "string" ? c.header : c.id)).join(",");
  const lines = rows.map((row) =>
    columns.map((column) => csvEscape(stringifyValue(getColumnValue(column, row)))).join(",")
  );
  return [header, ...lines].join("\r\n");
}

/** Serialize rows to a JSON string (uses accessors so it mirrors the grid). */
export function rowsToJson<TRow>(columns: TColumnDef<TRow>[], rows: TRow[]): string {
  const shaped = rows.map((row) => {
    const record: Record<string, unknown> = {};
    for (const column of columns) {
      record[column.id] = getColumnValue(column, row);
    }
    return record;
  });
  return JSON.stringify(shaped, null, 2);
}

/** Client-side substring search across the given fields. */
export function clientSearch<TRow>(rows: TRow[], term: string, fields?: (keyof TRow)[]): TRow[] {
  const trimmed = term.trim().toLowerCase();
  if (!trimmed) return rows;
  return rows.filter((row) => {
    if (fields && fields.length > 0) {
      return fields.some((field) => stringifyValue(row[field]).toLowerCase().includes(trimmed));
    }
    return stringifyValue(row).toLowerCase().includes(trimmed);
  });
}

function compareValues(a: unknown, b: unknown): number {
  if (typeof a === "number" && typeof b === "number") return a - b;
  return stringifyValue(a).localeCompare(stringifyValue(b), undefined, {
    numeric: true,
    sensitivity: "base",
  });
}

/** Client-side sort by a column's resolved value. */
export function clientSort<TRow>(
  rows: TRow[],
  column: TColumnDef<TRow> | undefined,
  order: "asc" | "desc"
): TRow[] {
  if (!column) return rows;
  const sorted = [...rows].sort((a, b) =>
    compareValues(getColumnValue(column, a), getColumnValue(column, b))
  );
  return order === "asc" ? sorted : sorted.reverse();
}
